import { createDecipheriv, pbkdf2Sync } from "crypto";
import { filterTenantRuntimeEnvVars } from "@gencow/core";

export function parseRuntimeEnvContent(
  content: string,
  internalToken: string | undefined,
): Record<string, string> | null {
  if (!content.trim()) return {};

  const parsePlainText = (text: string): Record<string, string> => {
    const vars: Record<string, string> = {};
    for (const line of text.split("\n")) {
      const trimmed = line.trim();
      if (!trimmed || trimmed.startsWith("#")) continue;
      const eqIndex = trimmed.indexOf("=");
      if (eqIndex < 1) continue;
      vars[trimmed.slice(0, eqIndex)] = trimmed.slice(eqIndex + 1);
    }
    return vars;
  };

  if (!content.startsWith("#GENCOW_ENC_V1")) {
    return parsePlainText(content);
  }

  if (!internalToken) {
    return null;
  }

  const encrypted = content.split("\n").slice(1).join("").trim();
  if (!encrypted) return {};

  const fileKey = pbkdf2Sync(internalToken, "gencow-env-runtime-file", 100_000, 32, "sha256");
  const raw = Buffer.from(encrypted, "base64");
  const iv = raw.subarray(0, 12);
  const authTag = raw.subarray(raw.length - 16);
  const ciphertext = raw.subarray(12, raw.length - 16);

  const decipher = createDecipheriv("aes-256-gcm", fileKey, iv, { authTagLength: 16 });
  decipher.setAuthTag(authTag);
  const plaintext = Buffer.concat([decipher.update(ciphertext), decipher.final()]).toString("utf8");

  return parsePlainText(plaintext);
}

export function applyRuntimeEnvSnapshot(
  targetEnv: NodeJS.ProcessEnv,
  runtimeEnvKeys: Set<string>,
  nextVars: Record<string, string>,
): number {
  const { allowed } = filterTenantRuntimeEnvVars(nextVars);
  const nextKeys = new Set(Object.keys(allowed));
  let loaded = 0;

  for (const key of runtimeEnvKeys) {
    if (!nextKeys.has(key)) {
      delete targetEnv[key];
    }
  }

  for (const [key, value] of Object.entries(allowed)) {
    targetEnv[key] = value;
    loaded++;
  }

  runtimeEnvKeys.clear();
  for (const key of nextKeys) {
    runtimeEnvKeys.add(key);
  }

  return loaded;
}
